import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, ChevronDown, ChevronRight, GraduationCap, History, Mail, Settings, ShieldCheck, Volume1, Volume2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import VerifyPage from "../pages/VerifyPage";
import RulesPage from "../pages/RulesPage";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { AssetImage } from "./AssetImage";
import { SimpleCard } from "./table/SimpleCard";
import { cardAssetId } from "../assets/assetManifest";
import { FELT_IDS } from "../assets/assetManifest";
import { DEALERS } from "./table/Dealer";
import { LedgerDialog } from "./lobby/LedgerDialog";
import { HandReplay } from "./table/HandReplay";
import { ProfileSection } from "./ProfileSection";
import { RewardsSection } from "./RewardsSection";
import { WalletSection } from "./WalletSection";
import { FriendsList } from "./lobby/FriendsList";
import { NotificationsSection } from "./NotificationsSection";
import { AutoChipsSection } from "./AutoChipsSection";
import { VipGamesSection } from "./VipGamesSection";
import { api, C } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { JoinButton } from "./JoinDialog";

const useLocalSetting = (key, fallback) => {
  const [v, setV] = useState(() => localStorage.getItem(key) ?? fallback);
  return [v, (nv) => { localStorage.setItem(key, nv); setV(nv); window.dispatchEvent(new CustomEvent("pfp-setting", { detail: { key, value: nv } })); }];
};

const Row = ({ label, hint, children, testId }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-amber-200/10" data-testid={testId}>
    <div><div className="text-sm text-amber-50">{label}</div>{hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}</div>
    {children}
  </div>
);

const Item = ({ icon: Icon, label, hint, onClick, disabled, testId }) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled}
    className="w-full flex items-center gap-3 py-3 border-b border-amber-200/10 text-left disabled:opacity-50 hover:bg-amber-500/5 px-1 rounded-md">
    <Icon className="w-4 h-4 gold-text shrink-0" />
    <div className="flex-1"><div className="text-sm text-amber-50">{label}</div>{hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}</div>
    <ChevronRight className="w-4 h-4 text-slate-500" />
  </button>
);

const GuestLock = ({ what }) => (
  <div data-testid={`guest-lock-${what}`} className="flex items-center justify-between gap-2 text-[11px] text-slate-400 mt-2">
    <span>Members only — join free to change {what === "deck" ? "card faces" : "table colour"}.</span><JoinButton testId={`guest-lock-${what}-join`} />
  </div>
);

const SettingsSection = ({ atTable, onVerifier, onPage, onLedger, onReplay }) => {
  const [open, setOpen] = useState(false);
  const guest = !!useAuth().user?.guest;
  const nav = useNavigate();
  const [sound, setSound] = useLocalSetting("pfp_sound", "on");
  const [musicOn, setMusicOn] = useLocalSetting("pfp_music", "on");
  const [musicVol, setMusicVol] = useLocalSetting("pfp_music_volume", "0.18");
  const [voice, setVoice] = useLocalSetting("pfp_dealer_voice", "on");
  const [felt, setFelt] = useLocalSetting("pfp_felt", "table_felt_green");
  const [dealer, setDealer] = useLocalSetting("pfp_dealer", "CLASSIC");
  const [deck, setDeck] = useLocalSetting("pfp_deck", "simple");
  return (
    <section data-testid="drawer-settings">
      <button data-testid="settings-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full flex items-center justify-between py-1 text-left">
        <div className="text-[11px] uppercase tracking-[0.3em] gold-text flex items-center gap-2"><Settings className="w-3.5 h-3.5" /> Settings</div>
        <span className="text-[10px] text-slate-500 flex items-center gap-1">ledger, replays, rules, cards, sound &amp; table <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {!open ? null : (
      <div className="mt-2">
      <section data-testid="drawer-items">
        <Item icon={BookOpen} label="Ledger" hint="Every chip movement, newest first" onClick={onLedger} testId="drawer-ledger-button" />
        <Item icon={History} label="Hand replay" hint="Your last hands on any table, or look up any hand by its #tag" onClick={onReplay} testId="drawer-replay-button" />
        <Item icon={GraduationCap} label="Game rules" hint={atTable ? "Every game explained — opens over the table" : "Every game explained step by step"} onClick={() => onPage("rules")} testId="drawer-rules-page-button" />
        <Item icon={Mail} label="Contact us" hint="Club enquiries, your account or a technical problem" onClick={() => nav("/contact")} testId="drawer-contact-button" />
      </section>
      <Item icon={ShieldCheck} label="Shuffle verification" hint={atTable ? "Seeds, nonces and the full deck of the previous hand" : "Open a table to verify its shuffles"} disabled={!atTable}
        onClick={onVerifier} testId="drawer-verify-button" />
      <Item icon={ShieldCheck} label="Verify it yourself" hint={atTable ? "How the shuffle is locked in — opens over the table" : "How the shuffle is locked in and how to check it"} onClick={() => onPage("verify")} testId="drawer-verify-page-button" />
      <div className="py-3 border-b border-amber-200/10" data-testid="setting-deck">
        <div className="text-sm text-amber-50 mb-2">Card faces</div>
        <div className={`flex gap-2 ${guest ? "opacity-40 pointer-events-none" : ""}`}>
          {[["simple", "Clear", "Big rank, big pip — easy on the eye"], ["art", "Classic art", "The illustrated deck"]].map(([k, label, hint]) => (
            <button key={k} data-testid={`deck-${k}`} onClick={() => setDeck(k)}
              className={`flex-1 rounded-lg border p-2 flex items-center gap-2 text-left ${deck === k ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
              {k === "simple" ? <SimpleCard card="Ah" height={40} /> : <AssetImage id={cardAssetId("Ah")} height={40} className="rounded-sm" />}
              <span><span className="text-xs text-amber-50 block">{label}</span><span className="text-[10px] text-slate-400">{hint}</span></span>
            </button>
          ))}
        </div>
        {guest && <GuestLock what="deck" />}
      </div>
      <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] gold-text mb-2 mt-4"><Settings className="w-4 h-4" /> Table &amp; sound</h3>
      <Row label="Sounds" hint="Chips, cards and alerts" testId="setting-sounds">
        <Switch data-testid="sounds-toggle" checked={sound === "on"} onCheckedChange={(c) => setSound(c ? "on" : "off")} />
      </Row>
      <div className="py-3 border-b border-amber-200/10" data-testid="setting-music">
        <div className="flex items-center gap-3">
          <div className="flex-1"><div className="text-sm text-amber-50">Background music</div><div className="text-[11px] text-slate-400 mt-0.5">The looping soundtrack — off means off, on every screen</div></div>
          <Switch data-testid="music-toggle" checked={musicOn === "on"} onCheckedChange={(c) => setMusicOn(c ? "on" : "off")} />
        </div>
        <div className={`flex items-center gap-3 mt-3 ${musicOn === "on" ? "" : "opacity-40 pointer-events-none"}`}>
          <Volume1 className="w-4 h-4 text-slate-400 shrink-0" />
          <Slider data-testid="music-volume" value={[Math.round(parseFloat(musicVol) * 100)]} min={0} max={100} step={5} className="flex-1"
            onValueChange={([v]) => setMusicVol(String(v / 100))} />
          <Volume2 className="w-4 h-4 text-slate-400 shrink-0" />
          <span data-testid="music-volume-value" className="font-mono text-[11px] text-amber-200 w-9 text-right">{Math.round(parseFloat(musicVol) * 100)}%</span>
        </div>
      </div>
      <Row label="Dealer's voice" hint="Spoken action calls at the table" testId="setting-voice">
        <Switch data-testid="dealer-voice-toggle" checked={voice === "on"} onCheckedChange={(c) => setVoice(c ? "on" : "off")} />
      </Row>
      <div className="py-3 border-b border-amber-200/10" data-testid="setting-felt">
        <div className="text-sm text-amber-50 mb-2">Table colour</div>
        <div className={`flex gap-2 ${guest ? "opacity-40 pointer-events-none" : ""}`}>
          {FELT_IDS.map((id) => (
            <button key={id} data-testid={`felt-${id.split("_")[2]}`} onClick={() => setFelt(id)}
              className={`flex-1 rounded-lg border overflow-hidden ${felt === id ? "border-amber-400" : "border-white/10 hover:border-white/30"}`}>
              <AssetImage id={id} width={120} className="w-full h-12 object-cover" />
            </button>
          ))}
        </div>
        {guest && <GuestLock what="felt" />}
      </div>
      <div className="py-3 border-b border-amber-200/10" data-testid="setting-dealer">
        <div className="text-sm text-amber-50 mb-2">Dealer</div>
        <div className="flex gap-2">
          {Object.entries(DEALERS).map(([k, d]) => (
            <button key={k} data-testid={`dealer-skin-${k}`} onClick={() => setDealer(k)}
              className={`flex-1 rounded-lg border p-2 flex items-center gap-2 ${dealer === k ? "border-amber-400 bg-amber-500/10" : "border-white/10 hover:border-white/30"}`}>
              <AssetImage id={d.asset} width={40} className="rounded-full bg-black/40" /><span className="text-xs">{d.name}</span>
            </button>
          ))}
        </div>
      </div>
      </div>
      )}
    </section>
  );
};

// Slim tab on the left edge; opens the side pane. Ledger opens in its own window (dialog), 25 entries at a time.
export const SidePane = () => {
  const { user } = useAuth();
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);
  const [page, setPage] = useState(null); // { page: "verify" | "rules", game? } — opened in a window over the table
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const atTable = pathname.startsWith("/table/");
  const [unread, setUnread] = useState(0);
  // the table's own panels link here too ("How to verify it yourself", "protocol", the "!" beside the table name):
  // same window, never off the table
  useEffect(() => {
    const onPage = (e) => {
      const which = e.detail?.page || "verify";
      const game = e.detail?.game;
      if (atTable) setPage({ page: which, game, ctx: e.detail?.ctx });
      else nav(`/${which}${game ? `?game=${game}` : ""}`, { state: { from: pathname } });
    };
    window.addEventListener("pfp-open-page", onPage);
    return () => window.removeEventListener("pfp-open-page", onPage);
  }, [atTable, nav, pathname]);
  // a menu entry opens as a window over the table, or as its own page (with a way back) anywhere else
  const openPage = (which) => { close(); if (atTable) setPage({ page: which }); else nav(`/${which}`, { state: { from: pathname } }); };
  useEffect(() => {
    const poll = () => api.get(C.ApiRoutes.MY_NOTIFICATIONS_UNREAD).then((r) => setUnread(r.data.unread)).catch(() => {});
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button data-testid="side-pane-tab" aria-label="Open menu"
            className={`menu-tab fixed left-0 top-[128px] z-40 rounded-r-2xl pl-2 pr-2.5 pb-7 ${unread > 0 ? "pt-10" : "pt-7"} flex items-center gap-1.5 hover:pl-3.5 transition-[padding]`}>
            <ChevronRight className="w-4 h-4 menu-tab-glyph" />
            <span className="menu-tab-text text-[12px] font-display font-black tracking-[0.35em] uppercase" style={{ writingMode: "vertical-rl" }}>Menu</span>
            {unread > 0 && <span data-testid="menu-unread-dot" title={`${unread} unread notification${unread === 1 ? "" : "s"}`} className="absolute top-1.5 left-1/2 -translate-x-1/2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 border-2 border-amber-100 text-white text-[11px] font-black leading-none flex items-center justify-center shadow-[0_0_10px_rgba(220,38,38,0.8)] animate-pulse">{unread > 9 ? "9+" : unread}</span>}
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[400px] sm:max-w-[400px] gold-card border-r text-slate-100 overflow-y-auto pane-scroll" data-testid="side-pane">
          <SheetHeader><SheetTitle className="font-display text-amber-50">Players Room</SheetTitle></SheetHeader>
          <div className="mt-6 flex flex-col gap-8">
            <ProfileSection />
            <RewardsSection />
            <WalletSection />
            <FriendsList />
            <AutoChipsSection />
            <SettingsSection atTable={atTable} onPage={openPage}
              onLedger={() => setLedgerOpen(true)} onReplay={() => { close(); setReplayOpen(true); }}
              onVerifier={() => { close(); window.dispatchEvent(new Event("pfp-open-verifier")); }} />
            <VipGamesSection />
            <NotificationsSection onUnread={setUnread} />
          </div>
        </SheetContent>
      </Sheet>
      <LedgerDialog open={ledgerOpen} onOpenChange={setLedgerOpen} />
      {replayOpen && <HandReplay myUserId={user?.user_id} onClose={() => setReplayOpen(false)} />}
      <Dialog open={!!page} onOpenChange={(o) => !o && setPage(null)}>
        <DialogContent className="max-w-3xl w-[94vw] max-h-[88vh] overflow-y-auto gold-card border-amber-300/20 p-0" data-testid={`overlay-page-${page?.page || "none"}`}>
          <DialogHeader className="sr-only"><DialogTitle>{page?.page === "rules" ? "Game rules" : "Verify it yourself"}</DialogTitle></DialogHeader>
          {page?.page === "rules" ? <RulesPage embedded game={page.game} ctx={page.ctx} /> : page?.page === "verify" ? <VerifyPage embedded /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
